import React, { useEffect, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { adminLogin, listCases, updateCase, listConsultants, listLibrary, adminListMothers } from '../lib/api';
import { LIBRARY_CATEGORIES } from '../data/library';
import { formatKL } from '../lib/timezone';
import { Lock, LogOut, RefreshCw, BookOpen, Building2, ClipboardList, Upload, Edit3, ChevronRight, CheckCircle2, CircleSlash, Users } from 'lucide-react';
import { toast } from 'sonner';
import RiskBadge from '../components/RiskBadge';
import { CONCERN_OPTIONS, GOAL_CONFIRM, getAllConcernIds } from '../data/combinedConcerns';
import CaseDetailDrawer from '../components/admin/CaseDetailDrawer';
import LibraryEditDrawer from '../components/admin/LibraryEditDrawer';
import MotherDetailDrawer from '../components/admin/MotherDetailDrawer';

const TOKEN_KEY = 'admin_token';
const STATUSES = [
  'new_lead',
  'pending_review',
  'pending_assignment',
  'assigned',
  'consultant_accepted',
  'customer_contacted',
  'consultation_scheduled',
  'service_completed',
  'reassigned',
  'closed',
];

export default function Admin() {
  const { lang, t } = useLanguage();
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [password, setPassword] = useState('');
  const [cases, setCases] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('cases');
  const [activeCaseId, setActiveCaseId] = useState(null);
  const [library, setLibrary] = useState([]);
  const [editingResource, setEditingResource] = useState(null);
  const [mothers, setMothers] = useState([]);
  const [activeMotherId, setActiveMotherId] = useState(null);

  const loadLibrary = async () => {
    try {
      const lib = await listLibrary();
      setLibrary(lib);
    } catch {
      // public endpoint; toast only if we really fail repeatedly
    }
  };

  const loadMothers = async () => {
    try {
      const ms = await adminListMothers(token);
      setMothers(ms);
    } catch {
      // ignore: mothers list is non-critical, surfaced separately
    }
  };

  const refresh = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [c, k] = await Promise.all([listCases(token), listConsultants()]);
      setCases(c);
      setConsultants(k);
      await Promise.all([loadLibrary(), loadMothers()]);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
        toast.error(lang === 'zh' ? '登录已失效' : 'Session expired');
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  useEffect(() => {
    if (!token) return;
    // Defer to a microtask so we don't synchronously set state from inside
    // the effect (some linters flag that pattern).
    const id = setTimeout(() => refresh(), 0);
    return () => clearTimeout(id);
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await adminLogin(password);
      localStorage.setItem(TOKEN_KEY, res.token);
      setToken(res.token);
      toast.success(lang === 'zh' ? '登录成功' : 'Logged in');
    } catch {
      toast.error(lang === 'zh' ? '密码错误' : 'Invalid password');
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setCases([]);
  };

  const handleAssign = async (caseId, consultantId) => {
    try {
      await updateCase(caseId, { assigned_consultant_id: consultantId, status: 'assigned' }, token);
      toast.success(lang === 'zh' ? '已分配' : 'Assigned');
      refresh();
    } catch {
      toast.error('Update failed');
    }
  };

  const handleStatus = async (caseId, status) => {
    try {
      await updateCase(caseId, { status }, token);
      toast.success(lang === 'zh' ? '已更新' : 'Updated');
      refresh();
    } catch {
      toast.error('Update failed');
    }
  };

  if (!token) {
    return (
      <div className="container-app py-16 max-w-md">
        <div className="card-base p-8" data-testid="admin-login">
          <div className="w-12 h-12 rounded-2xl bg-terracotta-light flex items-center justify-center mb-4">
            <Lock size={20} className="text-terracotta" />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-ink mb-6">{t('admin_login_title')}</h1>
          <form onSubmit={login} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('admin_password')}
              className="input-base"
              required
              data-testid="admin-password-input"
            />
            <button type="submit" data-testid="admin-login-btn" className="btn-primary w-full">
              {t('admin_login_btn')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="container-app py-8 sm:py-12 animate-fade-in-up" data-testid="admin-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink">{t('admin_title')}</h1>
        <div className="flex gap-2">
          <button onClick={refresh} className="btn-outline" data-testid="admin-refresh">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={logout} className="btn-outline" data-testid="admin-logout">
            <LogOut size={16} className="mr-1" /> {t('admin_logout')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-testid="admin-tabs">
        {[
          { id: 'cases', icon: ClipboardList, label: t('admin_tab_cases') },
          { id: 'mothers', icon: Users, label: t('admin_tab_mothers') },
          { id: 'library', icon: BookOpen, label: t('admin_tab_library') },
          { id: 'company', icon: Building2, label: t('admin_tab_company') },
        ].map((tabItem) => {
          const Icon = tabItem.icon;
          return (
            <button
              key={tabItem.id}
              onClick={() => setTab(tabItem.id)}
              data-testid={`admin-tab-${tabItem.id}`}
              className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                tab === tabItem.id
                  ? 'bg-terracotta text-white'
                  : 'bg-paper border border-border text-ink-secondary hover:border-terracotta'
              }`}
            >
              <Icon size={14} /> {tabItem.label}
            </button>
          );
        })}
      </div>

      {tab === 'cases' && (cases.length === 0 ? (
        <div className="card-base p-12 text-center text-ink-tertiary" data-testid="no-cases">
          {t('admin_no_cases')}
        </div>
      ) : (
        <div className="space-y-4">
          {cases.map(({ case: cs, submission: sub }) => {
            const ids = getAllConcernIds(sub);
            const [primaryId, ...relatedIds] = ids;
            const concernLabel = (cid) => {
              const opt = CONCERN_OPTIONS.find((o) => o.id === cid);
              return opt ? (lang === 'zh' ? opt.zh : opt.en) : cid;
            };
            const goalOpt = sub.goal_confirmation
              ? GOAL_CONFIRM.options.find((o) => o.id === sub.goal_confirmation)
              : null;
            const goalLabel = goalOpt ? (lang === 'zh' ? goalOpt.zh : goalOpt.en) : null;
            return (
              <div
                key={cs.id}
                data-testid={`case-${cs.id}`}
                onClick={() => setActiveCaseId(cs.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') setActiveCaseId(cs.id); }}
                className="card-interactive p-5 sm:p-6 cursor-pointer"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <RiskBadge level={sub.risk_level || 'green'} />
                      <span className="text-xs uppercase tracking-wider text-ink-tertiary">
                        {sub.language?.toUpperCase()}
                      </span>
                      <span className="text-xs text-ink-tertiary">
                        {new Date(sub.created_at).toLocaleString()}
                      </span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-ink leading-snug" data-testid={`case-primary-${cs.id}`}>
                      {primaryId ? concernLabel(primaryId) : (sub.main_concern || '-')}
                    </h3>
                    {relatedIds.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`case-related-${cs.id}`}>
                        {relatedIds.map((r) => (
                          <span
                            key={r}
                            className="inline-block px-2 py-0.5 rounded-full bg-terracotta-light/50 text-[10px] uppercase tracking-wider text-terracotta font-medium"
                          >
                            {t('result_related_chip')} · {concernLabel(r)}
                          </span>
                        ))}
                      </div>
                    )}
                    {goalLabel && (
                      <div className="mt-2 text-[11px] text-ink-secondary" data-testid={`case-goal-${cs.id}`}>
                        <span className="font-semibold text-ink">{t('result_goal_label')}: </span>{goalLabel}
                      </div>
                    )}
                    <p className="text-sm text-ink-secondary mt-2">
                      {sub.postpartum_age || '-'} · {sub.location_type || '-'} {sub.location_text ? `(${sub.location_text})` : ''} · {sub.feeding_method || '-'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs px-3 py-1 rounded-full bg-sage-light text-sage font-semibold">
                      {t(`status_${cs.status}`)}
                    </span>
                    <ChevronRight size={16} className="text-ink-tertiary" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">
                      {t('admin_assign')}
                    </label>
                    <select
                      value={cs.assigned_consultant_id || ''}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => { e.stopPropagation(); handleAssign(cs.id, e.target.value); }}
                      data-testid={`assign-${cs.id}`}
                      className="input-base"
                    >
                      <option value="">— {t('admin_unassigned')} —</option>
                      {consultants.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">
                      {t('admin_status')}
                    </label>
                    <select
                      value={cs.status}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => { e.stopPropagation(); handleStatus(cs.id, e.target.value); }}
                      data-testid={`status-${cs.id}`}
                      className="input-base"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{t(`status_${s}`)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {sub.preferred_consultant && (
                  <p className="text-xs text-ink-tertiary">
                    {lang === 'zh' ? '客户首选：' : 'Customer preference: '} {sub.preferred_consultant}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {tab === 'mothers' && (
        <div data-testid="admin-mothers-tab">
          {mothers.length === 0 ? (
            <div className="card-base p-12 text-center text-ink-tertiary" data-testid="admin-mothers-empty">
              {t('admin_mothers_empty')}
            </div>
          ) : (
            <div className="space-y-3">
              {mothers.map((row) => {
                const m = row.mother;
                const consultant = consultants.find((c) => c.id === row.assigned_consultant_id);
                return (
                  <div
                    key={m.id}
                    data-testid={`admin-mother-row-${m.id}`}
                    onClick={() => setActiveMotherId(m.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') setActiveMotherId(m.id); }}
                    className="card-interactive p-5 cursor-pointer"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {row.latest_risk_level && <RiskBadge level={row.latest_risk_level} />}
                          <span className="text-xs text-ink-tertiary">
                            {formatKL(m.created_at)}
                          </span>
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-ink">{m.name}</h3>
                        <p className="text-sm text-ink-secondary">
                          {m.phone} · {lang === 'zh' ? '宝宝' : 'Baby'} {m.baby_birth_date}
                        </p>
                        <p className="text-xs text-ink-tertiary mt-1">
                          {row.submissions_count} self-check · {row.milk_logs_count} milk log
                          {row.latest_main_concern ? ` · ${row.latest_main_concern}` : ''}
                          {consultant ? ` · ${lang === 'zh' ? '顾问' : 'Consultant'}: ${consultant.name}` : ''}
                        </p>
                      </div>
                      <ChevronRight size={16} className="text-ink-tertiary flex-shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'library' && (
        <div data-testid="admin-library-tab">
          <div className="card-base p-5 mb-5 bg-sage-light/30 flex gap-3 items-start">
            <BookOpen size={18} className="text-sage flex-shrink-0 mt-0.5" />
            <p className="text-sm text-ink-secondary">{t('admin_lib_intro')}</p>
          </div>

          <div className="grid gap-3">
            {library.map((r) => {
              const cat = LIBRARY_CATEGORIES.find((c) => c.id === r.category);
              const hasMedia = !!(r.media_url && r.media_url.trim());
              return (
                <div
                  key={r.id}
                  data-testid={`admin-lib-row-${r.id}`}
                  className="card-base p-4 flex items-center justify-between gap-3 flex-wrap"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-subtle text-ink-secondary">
                        {r.type === 'video' ? (lang === 'zh' ? '视频' : 'Video') : r.type === 'image_guide' ? (lang === 'zh' ? '图文' : 'Image') : (lang === 'zh' ? '指南' : 'Guide')}
                      </span>
                      {cat && (
                        <span className="text-[10px] text-ink-tertiary">
                          · {lang === 'zh' ? cat.zh : cat.en}
                        </span>
                      )}
                      {r.duration && <span className="text-[10px] text-ink-tertiary">· {r.duration}</span>}
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                          hasMedia ? 'bg-sage-light text-sage' : 'bg-subtle text-ink-tertiary'
                        }`}
                        data-testid={`admin-lib-media-flag-${r.id}`}
                      >
                        {hasMedia
                          ? <><CheckCircle2 size={10} /> {t('admin_lib_has_media')}</>
                          : <><CircleSlash size={10} /> {t('admin_lib_no_media')}</>}
                      </span>
                    </div>
                    <h3 className="text-sm font-medium text-ink truncate">
                      {lang === 'zh' ? r.title_zh : r.title_en}
                    </h3>
                  </div>
                  <button
                    onClick={() => setEditingResource(r)}
                    data-testid={`admin-lib-edit-${r.id}`}
                    className="btn-outline text-sm"
                    aria-label={t('admin_lib_edit')}
                  >
                    <Edit3 size={14} className="mr-1" /> {t('admin_lib_edit')}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'company' && (
        <div data-testid="admin-company-tab">
          <div className="card-base p-5 mb-5 bg-sage-light/30 flex gap-3 items-start">
            <Building2 size={18} className="text-sage flex-shrink-0 mt-0.5" />
            <p className="text-sm text-ink-secondary">{t('admin_placeholder_msg')}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              disabled
              data-testid="admin-company-edit-about"
              className="card-base p-5 text-left opacity-60 cursor-not-allowed"
            >
              <Edit3 size={18} className="text-terracotta mb-3" />
              <h3 className="font-heading font-semibold text-ink mb-1">{t('admin_company_edit_about')}</h3>
              <p className="text-xs text-ink-tertiary">{t('admin_placeholder_msg')}</p>
            </button>
            <button
              disabled
              data-testid="admin-company-logos"
              className="card-base p-5 text-left opacity-60 cursor-not-allowed"
            >
              <Building2 size={18} className="text-sage mb-3" />
              <h3 className="font-heading font-semibold text-ink mb-1">{t('admin_company_manage_logos')}</h3>
              <p className="text-xs text-ink-tertiary">{t('admin_placeholder_msg')}</p>
            </button>
            <button
              disabled
              data-testid="admin-company-gallery"
              className="card-base p-5 text-left opacity-60 cursor-not-allowed"
            >
              <Upload size={18} className="text-terracotta mb-3" />
              <h3 className="font-heading font-semibold text-ink mb-1">{t('admin_company_manage_gallery')}</h3>
              <p className="text-xs text-ink-tertiary">{t('admin_placeholder_msg')}</p>
            </button>
          </div>
        </div>
      )}

      <CaseDetailDrawer
        caseId={activeCaseId}
        open={!!activeCaseId}
        onClose={() => setActiveCaseId(null)}
        consultants={consultants}
        token={token}
        onChanged={refresh}
      />

      <LibraryEditDrawer
        resource={editingResource}
        open={!!editingResource}
        onClose={() => setEditingResource(null)}
        token={token}
        onSaved={loadLibrary}
      />

      <MotherDetailDrawer
        motherId={activeMotherId}
        open={!!activeMotherId}
        onClose={() => setActiveMotherId(null)}
        token={token}
        consultants={consultants}
      />
    </div>
  );
}
